import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { syncStripeSubscription, useAction } from "wasp/client/operations";
import { trackUpgradeCompleted } from "../client/utils/posthog";
import useTranslation from "../client/hooks/useTranslation";
import { Button } from "../client/components/ui/button";

export default function CheckoutResultPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const [urlSearchParams] = useSearchParams();
  const status = urlSearchParams.get("status");
  const [isSyncing, setIsSyncing] = useState(status === "success");
  const [paymentPending, setPaymentPending] = useState(false);

  const syncSubscription = useAction(syncStripeSubscription);

  useEffect(() => {
    if (status !== "success") return;

    // `status=success` only means Stripe redirected us back — NOT that the
    // payment cleared. A failed 3DS authentication lands here too. So we sync
    // first and only report an upgrade if the server confirms a live plan;
    // otherwise the payment is still pending or failed.
    syncSubscription(undefined as any)
      .then((result) => {
        if (result?.subscriptionPlan) {
          trackUpgradeCompleted(result.subscriptionPlan, 0);
        } else {
          setPaymentPending(true);
        }
      })
      .catch(() => {/* silent — webhook handles it in prod */})
      .finally(() => {
        setIsSyncing(false);
      });
  }, []);

  // Only leave the page once we know the outcome. A pending/failed payment keeps
  // the user here with an explanation instead of dropping them on /account with
  // no feedback.
  useEffect(() => {
    if (status !== "success" || isSyncing || paymentPending) return;
    navigate("/account");
  }, [status, isSyncing, paymentPending, navigate]);

  useEffect(() => {
    if (status !== "canceled") return;
    const timer = setTimeout(() => navigate("/account"), 3000);
    return () => clearTimeout(timer);
  }, []);

  if (status !== "success" && status !== "canceled") {
    return <Navigate to="/account" />;
  }

  const c = t.pricing.checkout;

  return (
    <div className="mt-10 flex flex-col items-stretch sm:mx-6 sm:items-center">
      <div className="flex flex-col gap-4 px-4 py-8 text-center shadow-xl ring-1 ring-gray-900/10 sm:max-w-md sm:rounded-lg sm:px-10 dark:ring-gray-100/10">
        <h1 className="text-xl font-semibold">
          {status === "success" &&
            (paymentPending ? c.pendingTitle : c.successTitle)}
          {status === "canceled" && c.canceledTitle}
        </h1>
        <span>
          {status === "success" && paymentPending
            ? c.pendingDescription
            : status === "success" && isSyncing
              ? c.activating
              : c.redirecting}
        </span>
        {paymentPending && (
          <Button variant="default" onClick={() => navigate("/account")}>
            {c.backToAccount}
          </Button>
        )}
      </div>
    </div>
  );
}
